import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, Wallet } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const vacio = { nombre: "", concepto: "", monto_base: "", descuento_pct: 0, cuotas: 1, descripcion: "", activo: true };

export default function AdminPlanes() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);

  const cargar = () => api.get("/planes-pago").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    if (!form.nombre.trim()) return toast.error("Indique el nombre de la modalidad");
    if (!form.concepto.trim()) return toast.error("Indique el concepto que aparecerá en la factura");
    if (!form.monto_base || Number(form.monto_base) <= 0) return toast.error("Indique el monto base");
    const payload = {
      nombre: form.nombre, concepto: form.concepto, monto_base: Number(form.monto_base),
      descuento_pct: Number(form.descuento_pct || 0), cuotas: Number(form.cuotas || 1),
      descripcion: form.descripcion, activo: form.activo,
    };
    try {
      if (form.id) await api.put(`/planes-pago/${form.id}`, payload);
      else await api.post("/planes-pago", payload);
      toast.success(form.id ? "Modalidad actualizada" : "Modalidad creada");
      setForm(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (p) => {
    try {
      await api.delete(`/planes-pago/${p.id}`);
      toast.success("Modalidad eliminada");
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!rows) return <Loading />;

  return (
    <div data-testid="admin-planes">
      <PageTitle title="Modalidades de pago"
                 subtitle="Conceptos editables que se asignan a los estudiantes y se muestran en el portal de padres.">
        <Button className="rounded-full" onClick={() => setForm({ ...vacio })} data-testid="nueva-modalidad">
          <Plus className="mr-2 h-4 w-4" /> Nueva modalidad
        </Button>
      </PageTitle>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="lista-planes">
        {rows.map((p) => {
          const final = p.monto_base * (1 - (p.descuento_pct || 0) / 100);
          return (
            <article key={p.id} className="lift rounded-lg border border-border/60 bg-card p-6">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 text-primary">
                  <Wallet className="h-4 w-4" />
                  <h3 className="font-display text-base font-semibold">{p.nombre}</h3>
                </div>
                <Badge variant={p.activo ? "secondary" : "outline"}>{p.activo ? "activa" : "inactiva"}</Badge>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">{p.descripcion}</p>
              <div className="mt-5">
                {p.descuento_pct > 0 && (
                  <p className="text-sm text-muted-foreground line-through">{money(p.monto_base)}</p>
                )}
                <p className="font-display text-3xl font-extrabold tracking-tight">{money(final)}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {p.cuotas > 1 ? `${p.cuotas} cuotas de ${money(final / p.cuotas)}` : "pago único"}
                  {p.descuento_pct > 0 ? ` · ${p.descuento_pct}% descuento` : ""}
                </p>
                <p className="mt-2 text-xs uppercase tracking-widest text-muted-foreground">{p.concepto}</p>
              </div>
              <div className="mt-5 flex gap-2">
                <Button size="sm" variant="outline" className="rounded-full"
                        onClick={() => setForm({ ...p, monto_base: String(p.monto_base) })}
                        data-testid={`editar-plan-${p.id}`}>
                  <Pencil className="mr-1 h-3 w-3" /> Editar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => eliminar(p)} data-testid={`eliminar-plan-${p.id}`}>
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            </article>
          );
        })}
        {rows.length === 0 && <Empty testId="planes-vacios">Aún no hay modalidades de pago.</Empty>}
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="modal-plan">
          <DialogHeader>
            <DialogTitle className="font-display">{form?.id ? "Editar modalidad" : "Nueva modalidad"}</DialogTitle>
            <DialogDescription>
              El monto final se calcula aplicando el descuento y se divide entre las cuotas indicadas.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Nombre de la modalidad</Label>
                <Input value={form.nombre} data-testid="plan-nombre"
                       onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Concepto en la factura</Label>
                <Input value={form.concepto} data-testid="plan-concepto"
                       onChange={(e) => setForm({ ...form, concepto: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Monto base (RD$)</Label>
                <Input type="number" value={form.monto_base} data-testid="plan-monto"
                       onChange={(e) => setForm({ ...form, monto_base: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Descuento (%)</Label>
                <Input type="number" value={form.descuento_pct} data-testid="plan-descuento"
                       onChange={(e) => setForm({ ...form, descuento_pct: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Cantidad de cuotas</Label>
                <Input type="number" min="1" value={form.cuotas} data-testid="plan-cuotas"
                       onChange={(e) => setForm({ ...form, cuotas: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <select className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={form.activo ? "1" : "0"} data-testid="plan-activo"
                        onChange={(e) => setForm({ ...form, activo: e.target.value === "1" })}>
                  <option value="1">Activa</option>
                  <option value="0">Inactiva</option>
                </select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Descripción</Label>
                <Input value={form.descripcion || ""} data-testid="plan-descripcion"
                       onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              </div>
              <p className="sm:col-span-2 rounded-md bg-muted px-4 py-3 text-xs text-muted-foreground">
                Monto final: <strong>{money((Number(form.monto_base) || 0) * (1 - (Number(form.descuento_pct) || 0) / 100))}</strong>
                {Number(form.cuotas) > 1 && ` · ${form.cuotas} cuotas de ${money(((Number(form.monto_base) || 0) * (1 - (Number(form.descuento_pct) || 0) / 100)) / Number(form.cuotas))}`}
              </p>
              <Button className="sm:col-span-2 rounded-full py-6" onClick={guardar} data-testid="guardar-plan">
                Guardar modalidad
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
